import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatMXN } from "../lib/api";
import StatusPill from "../components/StatusPill";
import ContactoAdicional, { CONTACTO_VACIO } from "../components/ContactoAdicional";
import { usePlanActual } from "../lib/planes";
import { useAuth } from "../contexts/AuthContext";
import { useEstadoCfdi, claveCfdi, T as T_CFDI, T_HIST } from "../lib/cfdi";
import FichaFiscal from "../components/cfdi/FichaFiscal";
import HistorialCfdis from "../components/cfdi/HistorialCfdis";
import { toast } from "sonner";
import { ArrowLeft, Calendar, FileText, Sparkles, Phone, Mail, Tag, MessageCircle, Cake } from "lucide-react";

function calcAge(birthDate) {
  const b = new Date(`${birthDate}T00:00:00`);
  if (Number.isNaN(b.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - b.getFullYear();
  const m = today.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < b.getDate())) age--;
  return age;
}

export default function PatientProfile() {
  const { sinSofi } = usePlanActual(); // TEN.4: sin Sofi no hay conversaciones ni bandeja
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [error, setError] = useState(null);
  const [notes, setNotes] = useState("");
  const [contacto, setContacto] = useState(CONTACTO_VACIO);
  const [guardandoContacto, setGuardandoContacto] = useState(false);
  // CL.4 C: la ficha fiscal sólo aparece con la facturación a pacientes disponible (bandera) y
  // la capacidad `cfdi`. Apagada: la ficha del paciente queda como hoy.
  const { activeClinic, user, puede } = useAuth() || {};
  const cfdi = useEstadoCfdi(claveCfdi(activeClinic, user));

  const load = () =>
    api.get(`/patients/${id}`).then((r) => {
      setP(r.data);
      setNotes(r.data.notes || "");
      setContacto(Object.fromEntries(Object.keys(CONTACTO_VACIO).map((k) => [k, r.data[k] || ""])));
    }).catch(() => setError("No se pudo cargar el perfil del paciente"));
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  if (error) return <div className="p-8 text-rose-600">{error}</div>;
  if (!p) return <div className="p-8 text-slate-500">Cargando paciente…</div>;

  const saveNotes = async () => {
    await api.patch(`/patients/${id}`, { notes });
    toast.success("Notas guardadas");
  };

  const guardarContacto = async () => {
    setGuardandoContacto(true);
    try {
      const { data } = await api.patch(`/patients/${id}`, contacto);
      setP((prev) => ({ ...prev, ...data }));
      setContacto(Object.fromEntries(Object.keys(CONTACTO_VACIO).map((k) => [k, data[k] || ""])));
      toast.success("Contacto guardado");
    } catch (e) {
      toast.error("No se pudo guardar el contacto");
    } finally {
      setGuardandoContacto(false);
    }
  };

  const descargarDocumento = async (doc) => {
    let url;
    try {
      const { data } = await api.get(`/files/${doc.id}`, { responseType: "blob" });
      url = URL.createObjectURL(data);
      const enlace = document.createElement("a");
      enlace.href = url;
      enlace.download = doc.name;
      document.body.appendChild(enlace);
      enlace.click();
      enlace.remove();
    } catch {
      toast.error("No se pudo descargar el documento");
    } finally {
      if (url) setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  };

  // 🔴 Los CUATRO botones de "Siguiente acción sugerida" se RETIRARON (auditoría de copy,
  // 12 Sep 2026). Decían "Enviar saludo IA" / "Enviar seguimiento" / "Enviar campaña" /
  // "Enviar recordatorio" y, al pulsarlos, mostraban `"{acción}: enviado"` — sin UNA SOLA
  // llamada al backend. Nunca se envió nada.
  //
  // Es la misma familia que los botones de Recuperación, pero PEOR por dónde vive: ésta es
  // la ficha del paciente, la pantalla donde la recepción trabaja todo el día. Un "enviado"
  // falso aquí hace que nadie dé el seguimiento de verdad.
  //
  // Y dos de las cuatro etiquetas describían funciones que NO EXISTEN en absoluto:
  // "Reagendar y enviar plantilla de no-show" (Sofi no puede reagendar: sus únicas
  // herramientas son consultar servicios, consultar disponibilidad y crear cita) y
  // "Lanzar campaña de recuperación" (las campañas quedaron en "Próximamente").
  //
  // Lo que queda es la sugerencia como TEXTO, sin botón: sigue orientando a la recepción
  // sobre qué conviene hacer, sin afirmar que el sistema lo hizo. Las dos acciones que sí
  // existen —escribirle y agendar— ya están en esta misma pantalla y en la Agenda.
  const recommended = (() => {
    if (p.status === "Nuevo") return "Darle la bienvenida y ofrecerle agendar.";
    if (p.status === "No asistió") return "Contactarlo para reagendar su cita.";
    if (p.status === "Recuperación" || p.status === "Perdido") return "Retomar el contacto: lleva tiempo sin venir.";
    if (p.status === "Agendado") return "Confirmar su cita 24h antes.";
    return "Sin acción urgente.";
  })();

  return (
    <div className="p-6 lg:p-8 space-y-5 max-w-6xl mx-auto w-full">
      {/* ⚠️ COL.11 -- este enlace NO se gatea, y es una decisión, no un olvido del censo.
          Lleva a /app/pacientes, que exige `pacientes` -- pero esta misma ficha se carga
          con `GET /api/patients/{pid}`, que exige EXACTAMENTE la misma capacidad. Quien
          está viendo esta pantalla ya la tiene, así que el enlace no puede ofrecer una
          puerta cerrada; esconderlo sólo dejaría al usuario sin forma de volver. */}
      <Link to="/app/pacientes" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5" data-testid="patient-back">
        <ArrowLeft className="w-4 h-4" /> Volver a pacientes
      </Link>
      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-card flex flex-wrap items-center gap-5">
        <div className="w-16 h-16 rounded-full bg-blue-100 text-blue-700 grid place-items-center font-display font-bold text-2xl">{p.name[0]}</div>
        <div className="flex-1 min-w-[240px]">
          <h1 className="font-display text-2xl font-extrabold tracking-tight">{p.name}</h1>
          <div className="text-sm text-slate-500 flex flex-wrap items-center gap-4 mt-1">
            <span className="inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {p.phone}</span>
            <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {p.email || "—"}</span>
            <span className="inline-flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" /> {p.source}</span>
            <span className="inline-flex items-center gap-1.5" data-testid="patient-birth-date">
              <Cake className="w-3.5 h-3.5" />
              {p.birth_date
                ? `${new Date(`${p.birth_date}T00:00:00`).toLocaleDateString("es-MX")} (${calcAge(p.birth_date)} años)`
                : "No registrada"}
            </span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <StatusPill status={p.status} />
          <div className="text-xs text-slate-400">Valor estimado: <span className="font-semibold text-slate-700">{formatMXN(p.estimated_value)}</span></div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <ContactoAdicional value={contacto} onChange={setContacto} />
        <button onClick={guardarContacto} disabled={guardandoContacto} data-testid="contacto-guardar"
          className="mt-3 text-sm bg-brand-deep text-white px-3 py-1.5 rounded-lg disabled:opacity-60">Guardar</button>
      </div>

      {/* Recommended action */}
      <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-4 flex items-center gap-3" data-testid="patient-next-action">
        <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-700 grid place-items-center"><Sparkles className="w-4 h-4" /></div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest font-bold text-indigo-700">Siguiente acción sugerida</div>
          <div className="text-sm text-indigo-900 mt-0.5">{recommended}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Conversation */}
        {!sinSofi && (
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl shadow-card overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="font-display font-bold inline-flex items-center gap-2"><MessageCircle className="w-4 h-4 text-emerald-600" /> Historial de conversación</div>
            {p.conversation && (
              <Link to={`/app/inbox?conv=${encodeURIComponent(p.conversation.id)}`} className="text-sm font-medium text-brand-electric hover:text-blue-700">Ver bandeja →</Link>
            )}
          </div>
          {!p.conversation ? (
            <div className="p-8 text-center text-slate-500 text-sm">Aún no hay conversación con este paciente.</div>
          ) : (
            <div className="p-4 space-y-2 max-h-96 overflow-y-auto flex flex-col">
              {p.conversation.messages.map((m, i) => {
                const real = p.conversation.real;
                const fromPatient = real ? m.direccion === "inbound" : m.sender === "patient";
                const body = real ? m.display_body : m.body;
                const ts = real ? m.created_at : m.ts;
                return (
                <div key={real ? m.wamid || i : m.id} className={`flex ${fromPatient ? "justify-start" : "justify-end"}`}>
                  <div className={`bubble ${fromPatient ? "bubble-patient" : !real && m.sender === "ai" ? "bubble-ai" : "bubble-agent"}`}>
                    {real && m.message_type === "audio" ? (
                      <>
                        <div className="text-[10px] font-bold mb-0.5">Nota de voz</div>
                        {m.transcrita && (<>
                          <div>{body}</div>
                          <div className={`text-[10px] mt-1 ${fromPatient ? "text-slate-400" : "opacity-70"}`}>
                            Transcripción automática; puede tener errores.
                          </div>
                        </>)}
                      </>
                    ) : body}
                    <div className={`text-[10px] mt-1 ${fromPatient ? "text-slate-400" : "opacity-70"}`}>
                      {ts && new Date(ts).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" })}
                    </div>
                  </div>
                </div>
              );})}
            </div>
          )}
        </div>
        )}

        {/* Side cards */}
        <div className="space-y-5">
          <div className="bg-white border border-slate-200 rounded-2xl shadow-card">
            <div className="px-5 py-3 border-b border-slate-200 font-display font-bold inline-flex items-center gap-2 w-full"><Calendar className="w-4 h-4 text-brand-electric" /> Historial de citas</div>
            {p.appointment_history?.length ? (
              <ul className="divide-y divide-slate-100">
                {p.appointment_history.map((a) => (
                  <li key={a.id} className="px-5 py-3 flex items-center justify-between gap-2">
                    <div>
                      <div className="text-sm font-medium text-slate-900">{a.service_name}</div>
                      <div className="text-xs text-slate-500">{a.date} · {a.time} · {a.doctor_name}</div>
                    </div>
                    <StatusPill status={a.status} />
                  </li>
                ))}
              </ul>
            ) : (
              <div className="p-6 text-center text-slate-500 text-sm">Sin citas registradas.</div>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl shadow-card p-5">
            <div className="font-display font-bold inline-flex items-center gap-2 mb-2"><FileText className="w-4 h-4" /> Notas internas</div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={5}
              placeholder="Información clínica, preferencias, observaciones…"
              className="w-full text-sm rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="patient-notes-input"
            />
            <button onClick={saveNotes} className="mt-2 inline-flex items-center text-sm font-medium bg-brand-deep text-white px-3 py-1.5 rounded-lg hover:bg-slate-800" data-testid="patient-notes-save">
              Guardar notas
            </button>
          </div>

          {cfdi.disponible && puede?.("cfdi") && (
            <div className="bg-white border border-slate-200 rounded-2xl shadow-card p-5" data-testid="patient-fiscal">
              <div className="font-display font-bold mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4 text-brand-electric" /> {T_CFDI.fiscalTitulo}
              </div>
              <FichaFiscal patientId={id} />
            </div>
          )}

          {/* CL.4 C3: las facturas de ESTE paciente (mismo endpoint, filtro patient_id). Misma
              condición que la ficha fiscal: bandera + capacidad `cfdi`. */}
          {cfdi.disponible && puede?.("cfdi") && (
            <div className="bg-white border border-slate-200 rounded-2xl shadow-card p-5" data-testid="patient-cfdis">
              <div className="font-display font-bold mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4 text-brand-electric" /> {T_HIST.pacienteTitulo}
              </div>
              <HistorialCfdis patientId={id} embebido />
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-2xl shadow-card" data-testid="patient-documents">
            <div className="px-5 py-3 border-b border-slate-200 font-display font-bold inline-flex items-center gap-2 w-full">
              <FileText className="w-4 h-4 text-brand-electric" /> Documentos
            </div>
            {p.documents?.length ? (
              <ul className="divide-y divide-slate-100">
                {p.documents.map((doc) => (
                  <li key={doc.id} className="px-5 py-3">
                    <button onClick={() => descargarDocumento(doc)} className="text-sm font-medium text-brand-electric hover:text-blue-700">{doc.name}</button>
                    <div className="text-xs text-slate-500">{new Date(doc.uploaded_at).toLocaleDateString("es-MX")}</div>
                  </li>
                ))}
              </ul>
            ) : <div className="p-6 text-center text-slate-500 text-sm">Sin documentos.</div>}
          </div>

          {/* "Estatus de pago" RETIRADO (auditoria de copy, 12 Sep 2026).
              Mostraba "Saldo pendiente: $0.00" y "Sin facturas vencidas" con los DOS valores
              hardcodeados -- `formatMXN(0)` literal y una cadena fija. No existe ningun modulo
              de cobros ni de facturacion por paciente, asi que ese cero no era un dato: era una
              afirmacion financiera inventada sobre una persona concreta. Se retira entero en vez
              de dejarlo en "Proximamente" -- nadie ha decidido construir cobranza. */}
        </div>
      </div>
    </div>
  );
}
